import React from 'react';
import ZaloCallbackPage from './pages/ZaloCallbackPage';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';

import Dashboard from './pages/Dashboard.js';
import TopicListPage from './pages/TopicListPage.js';
import TopicSessionPage from './pages/TopicSessionPage.js';
import VocabWordBookPage from './pages/VocabWordBookPage';
import TopicSessionResultPage from './pages/TopicSessionResultPage.js';
import TestSelection from './pages/TestSelection.js';
import ExamAttempt from './pages/ExamAttempt.js';
import Login from './pages/Login.js';
import Register from './pages/Register.js';
import LearningCorner from './pages/LearningCorner.js';
import VerifyOtp from './pages/VerifyOtp.js';
import SetCredentials from './pages/SetCredentials.js';
import ExamHistory from './pages/ExamHistory.js';
import SkillPractice from './pages/SkillPractice.js';
import SkillPartDetailPage from './pages/SkillPartDetailPage.js';
import FullMockTest from './pages/FullMockTest.js';
import PersonalizedTests from './pages/PersonalizedTests.js';
import PersonalizedTestAttempt from './pages/PersonalizedTestAttempt.js';
import ReviewAttempt from './pages/ReviewAttempt.js';
import RecentActivityPage from './pages/RecentActivityPage.js';
import TopicGamePlayPage from './pages/TopicGamePlayPage.js';
import LibraryPartDetailPage from './pages/LibraryPartDetailPage.js';
import WeeklyReportPage from './pages/WeeklyReportPage.js';

import FullTestAttempt from './pages/FullTestAttempt.js';
import FullTestResultPage from './pages/FullTestResultPage.js';
import FullTestSummaryPage from './pages/FullTestSummaryPage.js';
import SettingsPage from './pages/SettingsPage.js';
import Onboarding from './pages/Onboarding.js';
import PlacementTestIntro from './pages/PlacementTestIntro.js';
import PlacementTestTopics from './pages/PlacementTestTopics.js';
import PlacementTest from './pages/PlacementTest.js';
import PlacementResult from './pages/PlacementResult.js';
import DebugQuiz from './pages/DebugQuiz.js';
import QuestQuizSessionPage from './pages/QuestQuizSessionPage.js';
import SkillMissionPage from './pages/SkillMissionPage.js';
import PrivateRoute from './components/PrivateRoute.js';
import { LanguageProvider } from './context/LanguageContext.js';

import './styles/global.css';
import './styles/Dashboard.css';
import './styles/TestSelection.css';
import './styles/ExamAttempt.css';
import './styles/Login.css';

// Legacy /topics/:tagId now lives at /library/topics/:tagId. Preserve query/state on redirect.
const RedirectTopicToLibrary = () => {
  const { tagId } = useParams();
  const location = useLocation();
  return <Navigate replace to={`/library/topics/${tagId}${location.search}`} state={location.state} />;
};

// Library merged into the unified word book (/vocab/words).
const LibraryRedirect = () => <Navigate replace to="/vocab/words" />;
const LibraryTopicRedirect = () => {
  const { topicId } = useParams();
  return <Navigate replace to={`/vocab/words?topic=${topicId}`} />;
};
const LevelWordsRedirect = () => {
  const { levelName } = useParams();
  return <Navigate replace to={`/vocab/words?level=${encodeURIComponent(levelName || '')}`} />;
};

function App() {
  return (
    <LanguageProvider>
      <Router>
        <div className="app-container">
          <Routes>
            <Route path="/login" element={<Login />} />

            <Route path="/auth/zalo/callback" element={<ZaloCallbackPage />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify-otp" element={<VerifyOtp />} />
            <Route path="/set-credentials" element={<SetCredentials />} />
            <Route path="/" element={<PrivateRoute />}>
              <Route path="/" element={<Navigate replace to="/dashboard" />} />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/placement-test-intro" element={<PlacementTestIntro />} />
              <Route path="/placement-test-topics" element={<PlacementTestTopics />} />
              <Route path="/placement-test" element={<PlacementTest />} />
              <Route path="/placement-result" element={<PlacementResult />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/topics" element={<TopicListPage />} />
              <Route path="/topics/:tagId" element={<RedirectTopicToLibrary />} />
              <Route path="/topics/:tagId/play" element={<TopicSessionPage />} />
              <Route path="/vocab/:levelName/play" element={<TopicSessionPage mode="level" />} />
              <Route path="/vocab/words" element={<VocabWordBookPage />} />
              <Route path="/vocab/:levelName/words" element={<LevelWordsRedirect />} />
              <Route path="/vocab/:levelName/result" element={<TopicSessionResultPage />} />
              <Route path="/topics/:tagId/result" element={<TopicSessionResultPage />} />
              <Route path="/learning-corner" element={<LearningCorner />} />
              <Route path="/test-selection" element={<TestSelection />} />
              <Route path="/skill-practice" element={<SkillPractice />} />
              <Route path="/skill-practice/part/:partId" element={<SkillPartDetailPage />} />
              <Route path="/full-mock-test" element={<FullMockTest />} />
              <Route path="/skill-map" element={<Navigate replace to="/library" />} />
              <Route path="/skill-map/*" element={<Navigate replace to="/library" />} />
              <Route path="/personalized-tests" element={<PersonalizedTests />} />
              <Route path="/recent-activity" element={<RecentActivityPage />} />
              <Route path="/library" element={<LibraryRedirect />} />
              <Route path="/library/topics/:topicId" element={<LibraryTopicRedirect />} />
              <Route path="/library/topics/:topicId/game/:gameType" element={<TopicGamePlayPage />} />
              <Route path="/library/part/:partId" element={<LibraryPartDetailPage />} />
              <Route path="/report" element={<WeeklyReportPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/debug-quiz" element={<DebugQuiz />} />
              <Route path="/quest/quiz-session" element={<QuestQuizSessionPage />} />
              <Route path="/mission/:entityType/:entityId" element={<SkillMissionPage />} />

              <Route path="/full-mock-test/:examId/attempt" element={<FullTestAttempt />} />
              <Route path="/full-mock-test/summary/:attemptId" element={<FullTestSummaryPage />} />
              <Route path="/full-mock-test/:attemptId/review" element={<FullTestAttempt isReviewMode={true} />} />
              <Route path="/full-mock-test/result/:attemptId" element={<FullTestResultPage />} />

              <Route path="/personalized-tests/:examId/attempt" element={<PersonalizedTestAttempt />} />
              <Route path="/personalized-tests/:attemptId/review" element={<FullTestAttempt isReviewMode={true} />} />

              <Route path="/exam-attempt/:examId" element={<ExamAttempt />} />
              <Route path="/exam-attempt/:attemptId" element={<ExamAttempt />} />

              <Route path="/exam-history/:examId" element={<ExamHistory />} />
              <Route path="/review/:attemptId" element={<FullTestAttempt isReviewMode={true} />} />
            </Route>
          </Routes>
        </div>
      </Router>
    </LanguageProvider>
  );
}

export default App;
